import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text } from '@chakra-ui/react'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiDeleteBinLine } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../../../hooks/useToast'
import type {
  DeleteThreadResponse,
  ThreadErrorResponse,
} from '../../../../interfaces'
import { UserContext } from '../../../../providers'

interface Props {
  isOpen: boolean
  fileId: string
  threadId: string
  getThreads(): void
  onClose(): void
}

const DeleteModal = ({
  isOpen,
  fileId,
  threadId,
  getThreads,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const { user } = useContext(UserContext)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const deleteThread = async (): Promise<void> => {
    const res = (await request.delete(
      `/api/media-library/${user?.clientNumber}/files/${fileId}/threads/${threadId}`,
    )) as ThreadErrorResponse | DeleteThreadResponse

    if (request.error) {
      toast({
        description:
          (res as ThreadErrorResponse).message ??
          t(
            'error_in_deleting_thread',
            'An error occurred while deleting the thread. Please try again.',
          ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      void getThreads()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={RiDeleteBinLine}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('delete_thread', 'Delete thread')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text color="appBattleshipGrey">
            {t(
              'delete_thread_description',
              'Are you sure? This will delete the thread and all its replies.',
            )}
          </Text>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={async () => {
              setSubmitting(true)
              await deleteThread()
              setSubmitting(false)
              onClose()
            }}
          >
            {t('delete', 'Delete')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteModal
